import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useRootStore } from '../../Store/useRootStore';
import { Theme } from '../../Theme/Theme';

const Container = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const UnitOption = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lighterGrey)};
  border-radius: 10px;
  background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : 'white')};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    border-color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
    background: ${(props) => (props.$selected ? `${Theme.Colors.primary}08` : Theme.Colors.lightestGrey)};
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const UnitIcon = styled.div<{ $selected: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    color: ${(props) => (props.$selected ? 'white' : Theme.Colors.warmGrey)};
  }
`;

const UnitContent = styled.div`
  flex: 1;
`;

const UnitName = styled.span<{ $selected?: boolean }>`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.textColor)};
`;

const UnitDescription = styled.span`
  display: block;
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin-top: 2px;
`;

const RadioIndicator = styled.div<{ $selected?: boolean }>`
  width: 22px;
  height: 22px;
  border-radius: 11px;
  border: 2px solid ${(props) => (props.$selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 5px;
    background: ${(props) => (props.$selected ? Theme.Colors.primary : 'transparent')};
  }
`;

const MetricIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 16.5 16.5 4a2.12 2.12 0 0 1 3 3L7 19.5a2.12 2.12 0 1 1-3-3z" />
    <path d="M14 6l4 4" />
    <path d="M11.5 8.5l1.5 1.5" />
    <path d="M9 11l1.5 1.5" />
    <path d="M6.5 13.5 8 15" />
  </svg>
);

const ImperialIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 7h10a2 2 0 0 1 2 2v8H5V9a2 2 0 0 1 2-2z" />
    <path d="M9 7V5h6v2" />
    <path d="M10 11v2" />
    <path d="M14 11v2" />
    <path d="M9 16h6" />
  </svg>
);

export type UnitSystemSelectorProps = {
  onSelect?: () => void;
};

export const UnitSystemSelector: React.FC<UnitSystemSelectorProps> = observer(({ onSelect }) => {
  const { t } = useTrans();
  const { remoteSettings, language } = useRootStore();

  const currentUnit: 'metric' | 'imperial' =
    (remoteSettings.get('unitSystem') as 'metric' | 'imperial') ??
    (language.useMetricSystem ? 'metric' : 'imperial');

  const handleSelect = useCallback(
    (unit: 'metric' | 'imperial') => {
      remoteSettings.set('unitSystem', unit);
      onSelect?.();
    },
    [remoteSettings, onSelect]
  );

  return (
    <Container>
      <UnitOption $selected={currentUnit === 'metric'} onClick={() => handleSelect('metric')}>
        <UnitIcon $selected={currentUnit === 'metric'}>
          <MetricIcon />
        </UnitIcon>
        <UnitContent>
          <UnitName $selected={currentUnit === 'metric'}>{t('profile.settings.metric', { defaultValue: 'Metric' })}</UnitName>
          <UnitDescription>kg, cm, km</UnitDescription>
        </UnitContent>
        <RadioIndicator $selected={currentUnit === 'metric'} />
      </UnitOption>

      <UnitOption $selected={currentUnit === 'imperial'} onClick={() => handleSelect('imperial')}>
        <UnitIcon $selected={currentUnit === 'imperial'}>
          <ImperialIcon />
        </UnitIcon>
        <UnitContent>
          <UnitName $selected={currentUnit === 'imperial'}>{t('profile.settings.imperial', { defaultValue: 'Imperial' })}</UnitName>
          <UnitDescription>lb, ft, mi</UnitDescription>
        </UnitContent>
        <RadioIndicator $selected={currentUnit === 'imperial'} />
      </UnitOption>
    </Container>
  );
});
